import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PostCard } from '../components/social/PostCard';
import { supabase } from '../integrations/supabase/client';
import { Post } from '../hooks/usePosts';
import { TopNavigation } from '../components/layout/TopNavigation';
import { BottomNavigation } from '../components/layout/BottomNavigation';

const PostDetail: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estado da busca anterior (para preservar no retorno)
  const searchState = location.state?.searchState;
  const isFromSearch = !!searchState;
  const isFromHome = !!location.state?.fromHome;

  useEffect(() => {
    if (postId) {
      fetchPost();
    }
  }, [postId]);

  const fetchPost = async () => {
    if (!postId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          id,
          content,
          created_at,
          user_id,
          visibility,
          profiles (
            full_name,
            belt_rank,
            profile_picture_url,
            username,
            main_academy_id,
            is_instructor,
            profession,
            academies!main_academy_id(name),
            profile_hobbies(hobby:hobbies(name))
          )
        `)
        .eq('id', postId)
        .single();

      if (error) throw error;

      if (data) {
        const profile = Array.isArray(data.profiles) ? data.profiles[0] : data.profiles;
        const formattedPost: Post = {
          id: data.id,
          content: data.content,
          created_at: data.created_at,
          user_id: data.user_id,
          visibility: data.visibility,
          author: {
            full_name: profile?.full_name || null,
            belt_rank: profile?.belt_rank || 'white',
            profile_picture_url: profile?.profile_picture_url || null,
            username: profile?.username || null,
            main_academy_id: profile?.main_academy_id || null,
            academy_name: (profile?.academies && !Array.isArray(profile.academies)) ? profile.academies.name || null : (Array.isArray(profile?.academies) ? profile.academies[0]?.name || null : null),
            is_instructor: profile?.is_instructor || false,
            profession: profile?.profession || null,
            interests: profile?.profile_hobbies?.map((h: any) => h.hobby?.name).filter(Boolean) || [],
          },
        };
        setPost(formattedPost);
      } else {
        setError('Post not found');
      }
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (searchState) {
      // Retornar para a busca com estado preservado
      navigate('/search', { 
        state: { 
          searchState,
          preserveSearch: true 
        } 
      });
    } else if (isFromHome) {
      // Retornar para a home
      navigate('/home');
    } else {
      // Retornar para a página anterior ou home
      navigate(-1);
    }
  };

  const handleDeletePost = () => {
    // Após deletar o post, retornar para a busca
    handleBack();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#160C0C]">
        <TopNavigation />
        <div className="pt-20 pb-20 px-4">
          <div className="max-w-2xl mx-auto">
            {/* Botão voltar - apenas se veio da busca ou da home */}
            {(isFromSearch || isFromHome) && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {isFromSearch ? 'Back to Search' : 'Back to Home'}
                </span>
              </button>
            )}
            
            {/* Loading skeleton */}
            <div className="bg-[#160C0C] rounded-2xl shadow-md py-5 mb-4 w-full max-w-2xl mx-auto animate-pulse">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-white/10 rounded-full"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-white/10 rounded w-32"></div>
                  <div className="h-3 bg-white/10 rounded w-24"></div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-white/10 rounded w-full"></div>
                <div className="h-4 bg-white/10 rounded w-3/4"></div>
                <div className="h-4 bg-white/10 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#160C0C]">
        <TopNavigation />
        <div className="pt-20 pb-20 px-4">
          <div className="max-w-2xl mx-auto">
            {/* Botão voltar - apenas se veio da busca ou da home */}
            {(isFromSearch || isFromHome) && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {isFromSearch ? 'Back to Search' : 'Back to Home'}
                </span>
              </button>
            )}
            
            {/* Error state */}
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2 text-white font-sans">
                {error === 'Post not found' ? 'Post not found' : 'Failed to load post'}
              </h3>
              <p className="text-white/60">
                {error === 'Post not found' 
                  ? 'This post may have been deleted or is not accessible.'
                  : 'Failed to load post'
                }
              </p>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#160C0C]">
      <TopNavigation />
      <div className="pt-20 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Botão voltar - apenas se veio da busca ou da home */}
          {(isFromSearch || isFromHome) && (
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isFromSearch ? 'Back to Search' : 'Back to Home'}
              </span>
            </button>
          )}
          
          {/* Post */}
          <PostCard 
            post={post} 
            onDelete={handleDeletePost}
          />
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};

export default PostDetail; 